<template lang="pug">
  .simpleModel
    vue-headful(
      :title="title"
    )
    el-row(:gutter="25" type="flex" justify="center")
      el-col(:lg="4" :md="4" :sm="4" :xs="4")
        .scenarioSelection
          el-radio-group(v-model="financialAssumptionsSwitch")
            el-row
              el-radio-button(label="Base Case") Base Case
            el-row
              el-radio-button(label="Spot Case") Spot Prices
            el-row
              el-radio-button(label="User Case") User Case
        el-row
          h3 Inputs
        el-popover(
          placement="right"
          v-model='isOpenFinancial'
          trigger="click"
        )
          .financial-inputs(v-if="loaded === true")
            financial-inputs(
              :financialAssumptionsSwitch="financialAssumptionsSwitch"
              :financialInputs="financialInputs"
              :baseCaseFinancialAssumptions="baseCaseFinancialAssumptions"
              @switchChange="financialAssumptionsSwitch = $event"
              :ignore="['delayStartYear']"
            )
          el-button(slot="reference") Financial
            i(class="el-icon-arrow-down el-icon--right")
        el-row
          h3 Settings
          el-radio-group(
            v-model="viewSwitch"
            size="small"
          )
            el-radio-button(label="Chart")
            el-radio-button(label="Table")
        el-row
          el-radio-group(
            v-model="currencySwitch"
            size="small"
          )
            el-radio-button(label="USD")
            el-radio-button(label="CAD")
        el-row
          el-radio-group(
            v-model="accountingSwitch"
            size="small"
          )
            el-radio-button(label="By-Product")
            el-radio-button(label="Co-Product")

      el-col(:lg="16" :md="16" :sm="16" :xs="16")
        .header
          h2  Dashboard
        .output(v-loading="loading")
          .table(
            v-if="viewSwitch === 'Table'"
          )
            el-table(
              v-loading="tableLoading"
              element-loading-text="Loading..."
              :data="data"
            )
              el-table-column(
                label="Name"
                prop="model.assetName"
              )
                template(
                  v-slot:default="table"
                )
                  router-link(:to="{name: 'model', params: { id: table.row.model.id }}") {{ table.row.model.assetName }}
              el-table-column(
                label="Net Present Value"
                prop="valuation.npv"
                :formatter="npvFormatter"
              )
              el-table-column(
                label="IRR"
                prop="valuation.irr"
                :formatter="irrFormatter"
              )
              el-table-column(
                label="AISC"
                :prop="aiscTableProp"
                :formatter="aiscFormatter"
              )
          .charts(
            v-if="viewSwitch === 'Chart'"
          )
            bubble-chart(
              :data="data"
              height=650
              :settings="chartSettings"
              :exchangeRate="exchangeRate"
            )
            el-row(type="flex" justify="center")
              el-col(:lg="22" :md="22" :sm="22" :xs="22")
                .select
                  el-row(v-if="viewSwitch === 'Chart'")
                    el-radio-group(
                      v-model="chartSwitch"
                      size="large"
                    )
                      el-radio-button(label="AISC-IRR")
                      el-radio-button(label="CAPITAL-IRR")
                      el-radio-button(label="PNAV-IRR")
                      el-radio-button(label="Capital Intensity-IRR")

</template>

<script>
import Slider from '@/components/slider'
import CashFlowChart from '../components/CashFlowChart'
import ProductionChart from '../components/ProductionChart'
import FinancialInputs from '../components/FinancialInputs'
import _ from 'lodash'
import modelApi from '../services/ModelApi'
import BubbleChart from '../components/BubbleChart'

export default {
  name: 'home',
  components: { FinancialInputs, CashFlowChart, ProductionChart, Slider, BubbleChart },
  data: function () {
    return {
      isOpenFinancial: false,
      title: 'Dashboard',
      chartSwitch: 'AISC-IRR',
      tableLoading: true,
      loaded: false,
      loading: true,
      financialAssumptionsSwitch: 'Base Case',
      financialInputs: {
        discountRate: 0.05,
        delayStartYear: 0,
        goldPrice: 1350,
        silverPrice: 17,
        USDCAD: 1.33
      },
      rawData: [],
      viewSwitch: 'Chart',
      currencySwitch: 'USD',
      accountingSwitch: 'By-Product',
      // Store assumptions for resetting
      baseCaseFinancialAssumptions: {},
      spotPrices: {},
      // Flag to build table after load
      // Information to create table
      aiscChartField: 'AISC',
      currencyLabels: {
        CAD: 'C$',
        USD: 'US$'
      },
      exchangeRate: 1
    }
  },
  created () {
    this.debouncedUpdateData = _.debounce(this.updateData, 300)
    let updateBase = this.updateData(this.inputs)
    let updateSpot = this.fetchSpot()
    this.baseCaseFinancialAssumptions = this.copy(this.financialInputs)
    Promise.all([
      updateBase,
      updateSpot
    ]).then(() => {
      this.loaded = true
      this.loading = false
    })
  },
  computed: {
    data () {
      return this.rawData.map(value => {
        let output = this.copy(value)
        // Market Cap comes from database in CAD
        output.owner.market_cap = value.owner.market_cap / this.financialInputs.USDCAD
        // TODO: Note that this assumes only one asset, which may not be the case.  How do we allocate across multiple assets?
        output.owner.pnav = output.owner.market_cap / value.valuation.npv * 1000
        return output
      })
    },
    aiscTableProp () {
      return 'lom.' + this.aiscChartField
    },
    inputs () {
      return Object.assign({}, this.financialInputs)
    },
    chartSettings () {
      if (this.chartSwitch === 'AISC-IRR') {
        return {
          x: { apiLocation: 'valuation', item: 'irr', multiplier: 100, label: 'IRR', axisLabel: 'Project IRR (%)', min: 10, max: 60 },
          y: { apiLocation: 'lom', item: this.aiscChartField, multiplier: 1 * this.exchangeRate, label: 'AISC', axisLabel: 'All-In-Sustaining Costs ($/oz)', min: 500, max: 1000, precision: 0 },
          z: { apiLocation: 'valuation', item: 'npv', multiplier: 1 / 1000 * this.exchangeRate, label: 'NPV', axisLabel: 'Net Present Value', min: 0, max: 2000 },
          title: 'All In Sustaining Costs vs IRR',
          subtitle: 'Bubble Size Based on NAV'
        }
      } else if (this.chartSwitch === 'CAPITAL-IRR') {
        return {
          x: { apiLocation: 'valuation', item: 'irr', multiplier: 100, label: 'IRR', axisLabel: 'Project IRR (%)', min: 10, max: 60 },
          y: { apiLocation: 'lom', item: 'capital', multiplier: 1 / 1000 * this.exchangeRate, label: 'Capital', axisLabel: 'LOM Capital ($ M)', min: 100, max: 1000, precision: 0 },
          z: { apiLocation: 'valuation', item: 'npv', multiplier: 1 / 1000 * this.exchangeRate, label: 'NPV', axisLabel: 'Net Present Value', min: 0, max: 2000 },
          title: 'Life of Mine Capital vs IRR',
          subtitle: 'Bubble Size Based on NAV'
        }
      } else if (this.chartSwitch === 'PNAV-IRR') {
        return {
          x: { apiLocation: 'valuation', item: 'irr', multiplier: 100, label: 'IRR', axisLabel: 'Project IRR (%)', min: 10, max: 60 },
          y: { apiLocation: 'owner', item: 'pnav', multiplier: 1, label: 'P/NAV', axisLabel: 'P / NAV', min: 0.0, max: 2, precision: 2 },
          z: { apiLocation: 'valuation', item: 'npv', multiplier: 1 / 1000 * this.exchangeRate, label: 'NPV', axisLabel: 'Net Present Value', min: 0, max: 2000 },
          title: 'P/NAV vs IRR',
          subtitle: 'Bubble Size Based on NAV'
        }
      } else if (this.chartSwitch === 'Capital Intensity-IRR') {
        return {
          x: { apiLocation: 'valuation', item: 'irr', multiplier: 100, label: 'IRR', axisLabel: 'Project IRR (%)', min: 10, max: 60 },
          y: { apiLocation: 'valuation', item: 'capitalIntensity', multiplier: 1 * this.exchangeRate, label: 'Capital Intensity', axisLabel: 'Capital Intensity (US$/oz AuEq)', min: 0.0, max: 2, precision: 2 },
          z: { apiLocation: 'valuation', item: 'npv', multiplier: 1 / 1000 * this.exchangeRate, label: 'NPV', axisLabel: 'Net Present Value', min: 0, max: 2000 },
          title: 'P/NAV vs IRR',
          subtitle: 'Bubble Size Based on NAV'
        }
      } else {
        return {}
      }
    }
  },
  methods: {
    fetchSpot () {
      return modelApi.fetchSpotPrices({}).then(response => {
        this.spotPrices = response.data
      })
    },
    npvFormatter (row, column, value) {
      return '$' + Math.round(value * this.exchangeRate / 1000).toLocaleString()
    },
    aiscFormatter (row, column, value) {
      return '$' + Math.round(value * this.exchangeRate).toLocaleString()
    },
    irrFormatter (row, column, value) {
      return Math.round(value * 100) + '%'
    },
    copy (object) {
      return JSON.parse(JSON.stringify(object))
    },
    millions (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round((field * multiplier) / 1000).toLocaleString()
    },
    number (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round(field * multiplier).toLocaleString()
    },
    percent (field, exchange) {
      let multiplier = 1
      if (exchange) {
        multiplier = this.exchangeRate
      }
      return Math.round(field * 100 * multiplier).toLocaleString() + '%'
    },
    round (number, decimals) {
      return (
        Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals)
      )
    },
    updateData (params) {
      return modelApi.fetchModels(
        params
      ).then(response => {
        this.rawData = response.data
        this.tableLoading = false
      })
    },
    dataOrSpot (data) {
      if (data === 0) {
        return this.spotPrices.USDCAD
      } else {
        return data
      }
    }
  },
  watch: {
    technicalInputs: {
      deep: true,
      handler () {
        if (this.loaded) {
          this.loading = true
          this.updateData(this.inputs, false).then(
            () => { this.loading = false }
          )
        }
        if (
          JSON.stringify(this.technicalInputs) ===
              JSON.stringify(this.baseCaseTechnicalAssumptions)
        ) {
          this.technicalAssumptionsSwitch = 'Base Case'
        } else {
          this.technicalAssumptionsSwitch = 'User Case'
        }
      }
    },
    financialInputs: {
      deep: true,
      handler () {
        if (this.loaded) {
          this.loading = true
          this.updateData(this.inputs, false).then(
            () => { this.loading = false }
          )
        }
        if (
          JSON.stringify(this.financialInputs) ===
              JSON.stringify(this.baseCaseFinancialAssumptions)
        ) {
          this.financialAssumptionsSwitch = 'Base Case'
        } else if (
          JSON.stringify(this.financialInputs) ===
              JSON.stringify(this.spotPrices)
        ) {
          this.financialAssumptionsSwitch = 'Spot Case'
        } else {
          this.financialAssumptionsSwitch = 'User Case'
        }
      }
    },
    currencySwitch (value) {
      if (value === 'CAD') {
        this.exchangeRate = this.financialInputs.USDCAD
      } else {
        this.exchangeRate = 1
      }
    },
    accountingSwitch (value) {
      if (value === 'By-Product') {
        this.aiscChartField = 'AISC'
      } else {
        this.aiscChartField = 'coProductAISC'
      }
    },
    financialAssumptionsSwitch (value) {
      if (value === 'Base Case') {
        this.financialInputs = this.copy(this.baseCaseFinancialAssumptions)
      } else if (value === 'Spot Case') {
        // TODO: Make more general.  Is there a way to update the dictionary?
        this.financialInputs.USDCAD = this.spotPrices.USDCAD
        this.financialInputs.goldPrice = this.spotPrices.goldPrice
        this.financialInputs.silverPrice = this.spotPrices.silverPrice
      }
    },
    loaded (val) {
      if (val) {
      }
    }
  }

}
</script>

<style>
.simpleModel {
  background: white;
}

.simpleModel .tooltip {
  font-family: sans-serif;
}

.simpleModel p {
  margin-bottom: 0 !important;
}

.simpleModel h1 {
  font-size: 40px;
  margin-bottom: 0;
}

.simpleModel h2 {
  font-size: 25px;
  font-weight: bold;
  margin-bottom: 0px;
}

.simpleModel h3 {
  font-size: 20px;
}

.simpleModel h4 {
  color: #555;
  font-weight: 100;
  font-size: 18px;
  margin-top: 2px;
}

.simpleModel .el-radio-group {
  margin-bottom: 10px;
  margin-right: 5px;
}

.simpleModel .el-tag {
  border-color: white;
  margin-left: 30px;
  margin-right: 30px;
  font-size: 20px;
  padding-top: 20px;
  padding-bottom: 12px;
  padding-right: 20px;
  padding-left: 20px;
  vertical-align: middle;
  display: block;
  height: 60px;
  line-height: 14px;
}

.simpleModel .el-divider {
  width: 100%;
  background-color: white;
  margin-top: 5px;
  margin-bottom: 5px;
}

.simpleModel .header {
  margin-top: 20px;
  height: 50px;
}

.simpleModel .table {
  margin-top: 50px;
}

.simpleModel .scenarioSelection .el-radio-group {
  margin-top: 40px;
}

.simpleModel .scenarioSelection .el-radio-button {
  margin-top: 30px;
  margin-bottom: 30px;
}

.simpleModel .scenarioSelection .el-radio-button .el-radio-button__inner {
  height: 60px;
  width: 200px;
  font-size: 20px;
  padding-right: 20px;
  padding-left: 20px;
  padding-bottom: 20px;
  padding-top: 20px;
}

@media screen and (max-width: 1366px) {
  .tooltip {
    display: none;
  }
}
</style>
